const { PAYMENT_TYPE, BANK_TRANSFER_REQUEST_STATUS } = require('arbitrip-common/general/constants');
const { RESERVATION_STATUS } = require('@arbitrip/constants/reservations');
const { BANK_TRANSFER_REQUEST_KIND } = require('@arbitrip/constants');
const { getBookingBankTransferRequestStatus } = require('./BankTransferRequestStatus');

// OB-19 — Account (`company_policy`) bank-transfer settlement on the web Bookings page.
// The server's `account_bank_transfer_available` already folds in finance-manager + active credit line.
// Agency users are excluded: `/agency/bookings` ignores `payment_scope`.

const MONTH_STATE = {
	OK: 'ok',
	ALERT: 'alert',
	BLOCK: 'block',
};

const HIGHLIGHTED_MONTH_STATES = [MONTH_STATE.ALERT, MONTH_STATE.BLOCK];

const ACCOUNT_PAYMENT_SCOPE = 'account';

function isAccountBankTransferMode(profile) {
	return !!profile?.account_bank_transfer_available && !profile?.agent;
}

// Owner ruling (OB-19): Account payment and pay-later are never both on for a company; if a profile
// carries both flags, Account wins, so the Unpaid/Paid tabs use the Account scope.
function isAccountBookingsScope(profile) {
	return isAccountBankTransferMode(profile);
}

function getSettlementKind(booking) {
	const paymentType = booking?.payment?.payment_type;
	if (paymentType === PAYMENT_TYPE.PAY_LATER) return BANK_TRANSFER_REQUEST_KIND.PAY_LATER;
	if (paymentType === PAYMENT_TYPE.COMPANY_POLICY) return BANK_TRANSFER_REQUEST_KIND.ACCOUNT;
	return null;
}

function isAccountBooking(booking) {
	return getSettlementKind(booking) === BANK_TRANSFER_REQUEST_KIND.ACCOUNT;
}

function isAccountPaidBooking(booking) {
	return isAccountBooking(booking) && booking?.payment?.manually_marked_paid === true;
}

// `credit_line` is only present on rows served by the Account scope, so an Account booking listed on
// any other tab (or made before the credit line) is never shown as debt.
function isAccountUnpaidBooking(profile, booking) {
	return (
		isAccountBankTransferMode(profile) &&
		booking?.status === RESERVATION_STATUS.APPROVED &&
		isAccountBooking(booking) &&
		booking?.payment?.manually_marked_paid !== true &&
		!!booking?.credit_line
	);
}

// Rows served by the Account scope always carry `credit_line` (an object, or null without a ledger row).
function isAccountScopeRow(booking) {
	return isAccountBooking(booking) && booking?.credit_line !== undefined;
}

function isAccountMonthHighlighted(booking) {
	return HIGHLIGHTED_MONTH_STATES.includes(booking?.credit_line?.month_state);
}

function isAccountBankTransferRequest(booking) {
	return booking?.bank_transfer_request?.kind === BANK_TRANSFER_REQUEST_KIND.ACCOUNT;
}

function isAccountPaymentRequested(booking) {
	return (
		isAccountBankTransferRequest(booking) &&
		getBookingBankTransferRequestStatus(booking) === BANK_TRANSFER_REQUEST_STATUS.SUBMITTED
	);
}

// A needs_correction request is still the one the agent submitted, so it keeps the ref + submitted-date
// meta line; its badge stays BankTransferNeedsCorrection (`isAccountPaymentRequested` picks the badge).
const PAYMENT_REQUESTED_META_STATUSES = [
	BANK_TRANSFER_REQUEST_STATUS.SUBMITTED,
	BANK_TRANSFER_REQUEST_STATUS.NEEDS_CORRECTION,
];

function showsAccountPaymentRequestedMeta(booking) {
	return (
		isAccountBankTransferRequest(booking) &&
		PAYMENT_REQUESTED_META_STATUSES.includes(getBookingBankTransferRequestStatus(booking))
	);
}

module.exports = {
	MONTH_STATE,
	ACCOUNT_PAYMENT_SCOPE,
	isAccountBankTransferMode,
	isAccountBookingsScope,
	getSettlementKind,
	isAccountBooking,
	isAccountPaidBooking,
	isAccountUnpaidBooking,
	isAccountScopeRow,
	isAccountMonthHighlighted,
	isAccountBankTransferRequest,
	isAccountPaymentRequested,
	showsAccountPaymentRequestedMeta,
};
